import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  AttachMoney as MoneyIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import ProfessionalTable from '../components/ProfessionalTable';
import AdminPageWrapper from './components/AdminPageWrapper';
import api from '../utils/api';
import { API } from '../config/apiConfig';

function AdminPaymentMethods() {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [countryConfigurations, setCountryConfigurations] = useState([]);
  
  // Dialog states
  const [paymentMethodDialog, setPaymentMethodDialog] = useState(false);
  const [countryConfigDialog, setCountryConfigDialog] = useState(false);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  const [editingCountryConfig, setEditingCountryConfig] = useState(null);
  
  // Form states
  const [paymentMethodForm, setPaymentMethodForm] = useState({
    paymentMethodName: '',
    isActive: true
  });
  
  const [countryConfigForm, setCountryConfigForm] = useState({
    paymentMethodId: '',
    countryId: '',
    processingFeePercentage: 0.025,
    processingFeeFixed: 0.30,
    minWithdrawalAmount: 10.00,
    maxWithdrawalAmount: 10000.00,
    feeAbsorptionThreshold: 5.00,
    processingTimeHours: 24,
    isInstantAvailable: false,
    instantFeePercentage: 0.01,
    currencyCode: 'USD',
    isActive: true
  });
  
  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load data
  useEffect(() => {
    loadPaymentMethods();
    loadCountries();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API.adminPaymentMethods || '/api/admin/payment-methods'}`);
      setPaymentMethods(response.data.data || []);
    } catch (error) {
      console.error('❌ Error loading payment methods:', error);
      showSnackbar('Failed to load payment methods', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await api.get(`${API.countries || '/api/countries'}`);
      const payload = response.data;
      const list = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
      setCountries(list);
    } catch (error) {
      console.error('❌ Error loading countries:', error);
    }
  };

  const loadPaymentMethodDetails = async (paymentMethodId) => {
    try {
      const response = await api.get(`${API.adminPaymentMethods || '/api/admin/payment-methods'}/${paymentMethodId}`);
      setSelectedPaymentMethod(response.data.data.paymentMethod);
      setCountryConfigurations(response.data.data.countryConfigurations || []);
    } catch (error) {
      console.error('❌ Error loading payment method details:', error);
      showSnackbar('Failed to load payment method details', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handlePaymentMethodSelect = (paymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    setSelectedTab(1); // Switch to Country Configurations tab
    loadPaymentMethodDetails(paymentMethod.PaymentMethodId);
  };

  const handleCreatePaymentMethod = () => {
    setEditingPaymentMethod(null);
    setPaymentMethodForm({
      paymentMethodName: '',
      isActive: true
    });
    setPaymentMethodDialog(true);
  };

  const handleEditPaymentMethod = (paymentMethod) => {
    setEditingPaymentMethod(paymentMethod);
    setPaymentMethodForm({
      paymentMethodName: paymentMethod.PaymentMethodName,
      isActive: paymentMethod.IsActive
    });
    setPaymentMethodDialog(true);
  };

  const handleSavePaymentMethod = async () => {
    try {
      if (editingPaymentMethod) {
        await api.put(`${API.adminPaymentMethods || '/api/admin/payment-methods'}/${editingPaymentMethod.PaymentMethodId}`, paymentMethodForm);
        showSnackbar('Payment method updated successfully');
      } else {
        await api.post(`${API.adminPaymentMethods || '/api/admin/payment-methods'}`, paymentMethodForm);
        showSnackbar('Payment method created successfully');
      }
      setPaymentMethodDialog(false);
      loadPaymentMethods();
    } catch (error) {
      console.error('❌ Error saving payment method:', error);
      showSnackbar(error.response?.data?.message || 'Failed to save payment method', 'error');
    }
  };

  const handleAddCountryConfig = () => {
    if (!selectedPaymentMethod) {
      showSnackbar('Please select a payment method first', 'warning');
      return;
    }
    
    setEditingCountryConfig(null);
    setCountryConfigForm({
      ...countryConfigForm,
      paymentMethodId: selectedPaymentMethod.PaymentMethodId
    });
    setCountryConfigDialog(true);
  };

  const handleEditCountryConfig = (config) => {
    setEditingCountryConfig(config);
    setCountryConfigForm({
      paymentMethodId: config.PaymentMethodId,
      countryId: config.CountryId,
      processingFeePercentage: config.ProcessingFeePercentage,
      processingFeeFixed: config.ProcessingFeeFixed,
      minWithdrawalAmount: config.MinWithdrawalAmount,
      maxWithdrawalAmount: config.MaxWithdrawalAmount,
      feeAbsorptionThreshold: config.FeeAbsorptionThreshold,
      processingTimeHours: config.ProcessingTimeHours,
      isInstantAvailable: config.IsInstantAvailable,
      instantFeePercentage: config.InstantFeePercentage,
      currencyCode: config.CurrencyCode,
      isActive: config.IsActive
    });
    setCountryConfigDialog(true);
  };

  const handleSaveCountryConfig = async () => {
    try {
      if (editingCountryConfig) {
        await api.put(`${API.adminPaymentMethods || '/api/admin/payment-methods'}/countries/${editingCountryConfig.PaymentMethodCountryId}`, countryConfigForm);
        showSnackbar('Country configuration updated successfully');
      } else {
        await api.post(`${API.adminPaymentMethods || '/api/admin/payment-methods'}/${countryConfigForm.paymentMethodId}/countries`, countryConfigForm);
        showSnackbar('Country configuration added successfully');
      }
      setCountryConfigDialog(false);
      if (selectedPaymentMethod) {
        loadPaymentMethodDetails(selectedPaymentMethod.PaymentMethodId);
      }
    } catch (error) {
      console.error('❌ Error saving country configuration:', error);
      showSnackbar(error.response?.data?.message || 'Failed to save country configuration', 'error');
    }
  };

  const handleDeleteCountryConfig = async (config) => {
    if (window.confirm(`Are you sure you want to delete the configuration for ${config.CountryName}?`)) {
      try {
        await api.delete(`${API.adminPaymentMethods || '/api/admin/payment-methods'}/countries/${config.PaymentMethodCountryId}`);
        showSnackbar('Country configuration deleted successfully');
        if (selectedPaymentMethod) {
          loadPaymentMethodDetails(selectedPaymentMethod.PaymentMethodId);
        }
      } catch (error) {
        console.error('❌ Error deleting country configuration:', error);
        showSnackbar('Failed to delete country configuration', 'error');
      }
    }
  };

  const paymentMethodColumns = [
    {
      header: 'Payment Method',
      accessor: 'PaymentMethodName',
      render: (name, row) => (
        <Box>
          <Typography fontWeight={500}>{name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {row.CountryCount} countries configured
          </Typography>
        </Box>
      )
    },
    {
      header: 'Status',
      accessor: 'IsActive',
      render: (isActive) => (
        <Chip 
          label={isActive ? 'Active' : 'Inactive'}
          color={isActive ? 'success' : 'error'}
          size="small"
        />
      ),
      style: { textAlign: 'center' }
    },
    {
      header: 'Created',
      accessor: 'CreatedAt',
      render: (date) => new Date(date).toLocaleDateString(),
      style: { textAlign: 'center' }
    },
    {
      header: 'Actions',
      accessor: 'PaymentMethodId',
      render: (id, row) => (
        <Box display="flex" gap={1} justifyContent="center">
          <Tooltip title="Configure Countries">
            <IconButton
              size="small"
              onClick={() => handlePaymentMethodSelect(row)}
              color="primary"
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditPaymentMethod(row)}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      style: { textAlign: 'center' }
    }
  ];

  const countryConfigColumns = [
    {
      header: 'Country',
      accessor: 'CountryName',
      render: (name, row) => (
        <Box>
          <Typography fontWeight={500}>{name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {row.CountryCode}
          </Typography>
        </Box>
      )
    },
    {
      header: 'Currency',
      accessor: 'CurrencyCode',
      render: (currency) => (
        <Chip label={currency} size="small" color="primary" variant="outlined" />
      ),
      style: { textAlign: 'center' }
    },
    {
      header: 'Fees',
      accessor: 'ProcessingFeePercentage',
      render: (percentage, row) => (
        <Box>
          <Typography variant="body2">
            {(percentage * 100).toFixed(2)}% + {row.CurrencyCode} {row.ProcessingFeeFixed}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Min: {row.CurrencyCode} {row.MinWithdrawalAmount}
          </Typography>
        </Box>
      )
    },
    {
      header: 'Processing',
      accessor: 'ProcessingTimeHours',
      render: (hours, row) => (
        <Box>
          <Typography variant="body2">
            {hours}h {row.IsInstantAvailable ? '(Instant available)' : ''}
          </Typography>
        </Box>
      )
    },
    {
      header: 'Status',
      accessor: 'IsActive',
      render: (isActive) => (
        <Chip 
          label={isActive ? 'Active' : 'Inactive'}
          color={isActive ? 'success' : 'error'}
          size="small"
        />
      ),
      style: { textAlign: 'center' }
    },
    {
      header: 'Actions',
      accessor: 'PaymentMethodCountryId',
      render: (id, row) => (
        <Box display="flex" gap={1} justifyContent="center">
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditCountryConfig(row)}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteCountryConfig(row)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      style: { textAlign: 'center' }
    }
  ];

  return (
    <AdminPageWrapper 
      title="Payment Methods Management" 
      subtitle="Manage payment methods and their country-specific configurations"
    >
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreatePaymentMethod}
        >
          Add Payment Method
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label="Payment Methods" icon={<MoneyIcon />} />
          <Tab 
            label="Country Configurations" 
            icon={<PublicIcon />}
            disabled={!selectedPaymentMethod}
          />
        </Tabs>
      </Box>

      {selectedTab === 0 && (
        <ProfessionalTable
          columns={paymentMethodColumns}
          data={paymentMethods.map(pm => ({ ...pm, id: pm.PaymentMethodId }))}
          loading={loading}
          emptyMessage="No payment methods found"
        />
      )}

      {selectedTab === 1 && selectedPaymentMethod && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Configurations for {selectedPaymentMethod.PaymentMethodName}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCountryConfig}
            >
              Add Country Configuration
            </Button>
          </Box>
          
          <ProfessionalTable
            columns={countryConfigColumns}
            data={countryConfigurations.map(config => ({ ...config, id: config.PaymentMethodCountryId }))}
            loading={false}
            emptyMessage="No country configurations found"
          />
        </Box>
      )}

      {/* Payment Method Dialog */}
      <Dialog open={paymentMethodDialog} onClose={() => setPaymentMethodDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPaymentMethod ? 'Edit Payment Method' : 'Add Payment Method'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Payment Method Name"
            value={paymentMethodForm.paymentMethodName}
            onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, paymentMethodName: e.target.value })}
            margin="normal"
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={paymentMethodForm.isActive}
                onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, isActive: e.target.checked })}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentMethodDialog(false)}>Cancel</Button>
          <Button onClick={handleSavePaymentMethod} variant="contained">
            {editingPaymentMethod ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Country Configuration Dialog */}
      <Dialog open={countryConfigDialog} onClose={() => setCountryConfigDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCountryConfig ? 'Edit Country Configuration' : 'Add Country Configuration'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Country</InputLabel>
              <Select
                value={countryConfigForm.countryId}
                onChange={(e) => setCountryConfigForm({ ...countryConfigForm, countryId: e.target.value })}
                disabled={!!editingCountryConfig}
              >
                {countries.map((country, idx) => {
                  const id = country.CountryID || country.CountryId || country.countryId || country.ID || country.Id || country.id;
                  const name = country.CountryName || country.Name || country.countryName || country.name || '';
                  const code = country.CountryCode || country.Code || country.countryCode || country.code || '';
                  if (!id) return null;
                  return (
                    <MenuItem key={id || idx} value={id}>
                      {name} {code ? `(${code})` : ''}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Currency Code"
              value={countryConfigForm.currencyCode}
              onChange={(e) => setCountryConfigForm({ ...countryConfigForm, currencyCode: e.target.value })}
              required
            />

            <TextField
              fullWidth
              label="Processing Fee Percentage"
              type="number"
              value={countryConfigForm.processingFeePercentage}
              onChange={(e) => setCountryConfigForm({ ...countryConfigForm, processingFeePercentage: parseFloat(e.target.value) })}
              inputProps={{ step: 0.0001, min: 0, max: 1 }}
              required
            />

            <TextField
              fullWidth
              label="Fixed Fee Amount"
              type="number"
              value={countryConfigForm.processingFeeFixed}
              onChange={(e) => setCountryConfigForm({ ...countryConfigForm, processingFeeFixed: parseFloat(e.target.value) })}
              inputProps={{ step: 0.01, min: 0 }}
              required
            />

            <TextField
              fullWidth
              label="Min Withdrawal Amount"
              type="number"
              value={countryConfigForm.minWithdrawalAmount}
              onChange={(e) => setCountryConfigForm({ ...countryConfigForm, minWithdrawalAmount: parseFloat(e.target.value) })}
              inputProps={{ step: 0.01, min: 0 }}
              required
            />

            <TextField
              fullWidth
              label="Max Withdrawal Amount"
              type="number"
              value={countryConfigForm.maxWithdrawalAmount}
              onChange={(e) => setCountryConfigForm({ ...countryConfigForm, maxWithdrawalAmount: parseFloat(e.target.value) })}
              inputProps={{ step: 0.01, min: 0 }}
              required
            />

            <TextField
              fullWidth
              label="Fee Absorption Threshold"
              type="number"
              value={countryConfigForm.feeAbsorptionThreshold}
              onChange={(e) => setCountryConfigForm({ ...countryConfigForm, feeAbsorptionThreshold: parseFloat(e.target.value) })}
              inputProps={{ step: 0.01, min: 0 }}
              required
            />

            <TextField
              fullWidth
              label="Processing Time (Hours)"
              type="number"
              value={countryConfigForm.processingTimeHours}
              onChange={(e) => setCountryConfigForm({ ...countryConfigForm, processingTimeHours: parseInt(e.target.value) })}
              inputProps={{ min: 1 }}
              required
            />

            <TextField
              fullWidth
              label="Instant Fee Percentage"
              type="number"
              value={countryConfigForm.instantFeePercentage}
              onChange={(e) => setCountryConfigForm({ ...countryConfigForm, instantFeePercentage: parseFloat(e.target.value) })}
              inputProps={{ step: 0.0001, min: 0, max: 1 }}
            />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={countryConfigForm.isInstantAvailable}
                    onChange={(e) => setCountryConfigForm({ ...countryConfigForm, isInstantAvailable: e.target.checked })}
                  />
                }
                label="Instant Payout Available"
              />
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={countryConfigForm.isActive}
                    onChange={(e) => setCountryConfigForm({ ...countryConfigForm, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCountryConfigDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveCountryConfig} variant="contained">
            {editingCountryConfig ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminPageWrapper>
  );
}

export default AdminPaymentMethods;
